import React, { ReactNode, memo } from 'react';
import { DOMProps } from '@react-types/shared';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';

import { cn } from '../../../lib/css/className/className';
import { useSidebarCollapsedState } from '../SidebarCollapsedStateProvider';
import { VStack } from '../../Stack';
import { TestProps } from '../../../internal/types/testTypes';
import { ListSection } from '../../List';
import { useSidebarIsNewMobileView } from '../SidebarIsNewMobileViewStateProvider';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';

getPlatformStyles({
    mobile: import('./SidebarSection@mobile.css'),
    desktop: import('./SidebarSection@desktop.css'),
});

const cnSidebarSection = cn('SidebarSection');

export interface SidebarSectionProps extends TestProps, DOMProps {
    /**
     * Дочерние элементы
     * @expectedNode `SidebarItem` | `SidebarButton` | `SidebarItemStub` |
     * `SidebarBalance` | `SidebarBalanceStub` | `SidebarUserProfileItem` |
     * `SidebarCollapseItem`
     */
    children?: ReactNode;
    /**
     * Дополнительный класс
     */
    className?: string;
    /**
     * Показывать разделитель между секциями в свёрнутом состоянии
     */
    showDividerInCollapsed?: boolean;
}

export const SidebarSection = memo<SidebarSectionProps>(function SidebarSection({
    children,
    className,
    showDividerInCollapsed,
    'data-testid': dataTestId,
}) {
    const { isCollapsed } = useSidebarCollapsedState();
    const isNewMobileView = useSidebarIsNewMobileView();

    const sizeSkinClass = getSizeSkin('s');
    const colorSkinClass = getColorSkin('gray-transparent-contour', {});

    if (isMobile() && isNewMobileView) {
        return <ListSection>{children}</ListSection>;
    }

    return (
        <VStack
            className={cnSidebarSection(
                '',
                {
                    collapsed: isCollapsed,
                    mobile: isMobile(),
                    withDivider: showDividerInCollapsed && isCollapsed,
                },
                [sizeSkinClass, colorSkinClass, className],
            )}
            data-testid={dataTestId}
        >
            {children}
        </VStack>
    );
});
